import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import ConfirmationModal from './ConfirmationModal';
import { adminApi } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useSuccessAlert, useErrorAlert, useWarningAlert } from './AlertSystem';
import { extractErrorMessage } from '../lib/utils';
import './AdminRoleManagement.css';

interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  isEditable: boolean;
  users?: Array<{
    id: string;
    name: string;
    email: string;
  }>;
  createdAt: string;
}

interface PermissionStructure {
  [category: string]: {
    permissions: string[];
    description: string;
  };
}

interface CreateRoleData {
  name: string;
  description: string;
  permissions: string[];
}

interface EditRoleData {
  name: string;
  description: string;
  permissions: string[];
}

const AdminRoleManagement: React.FC = () => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissionStructure, setPermissionStructure] = useState<PermissionStructure>({});
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [editRoleData, setEditRoleData] = useState<EditRoleData>({
    name: '',
    description: '',
    permissions: []
  });
  const [createRoleData, setCreateRoleData] = useState<CreateRoleData>({
    name: '',
    description: '',
    permissions: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Alert hooks
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();
  const showWarning = useWarningAlert();

  // Load roles and permission structure
  const loadRoles = async () => {
    try {
      setLoading(true);
      const [rolesResponse, permissionsResponse] = await Promise.all([
        adminApi.getRoles(),
        adminApi.getPermissionStructure()
      ]);

      if (rolesResponse.success && rolesResponse.data) {
        setRoles(rolesResponse.data);
      } else {
        console.error('Roles response error:', rolesResponse);
        const errorMsg = extractErrorMessage(rolesResponse);
        showError(errorMsg, 'Error Loading Roles');
      }

      if (permissionsResponse.success && permissionsResponse.data) {
        setPermissionStructure(permissionsResponse.data);
      } else {
        console.error('Permissions response error:', permissionsResponse);
        const errorMsg = extractErrorMessage(permissionsResponse);
        showError(errorMsg, 'Error Loading Permissions');
      }
    } catch (err) {
      console.error('Load roles error:', err);
      showError('Failed to connect to server', 'Connection Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'super admin': return '#ef4444';
      case 'superadmin': return '#ef4444';
      case 'admin': return '#8b5cf6';
      case 'user': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setEditRoleData({
      name: role.name,
      description: role.description || '',
      permissions: [...role.permissions]
    });
    setIsEditModalOpen(true);
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setCreateRoleData({
      name: '',
      description: '',
      permissions: []
    });
    setIsCreateModalOpen(true);
  };

  const handleDeleteRole = (role: Role) => {
    setRoleToDelete(role);
    setIsDeleteModalOpen(true);
  };

  const handleSaveRole = async () => {
    if (!selectedRole) return;

    // Validation
    if (!editRoleData.name.trim()) {
      showWarning('Role name is required', 'Validation Error');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // For now, use direct fetch to avoid type issues
      const token = localStorage.getItem('nuroveli_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/admin/roles/${selectedRole.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editRoleData.name,
          description: editRoleData.description,
          permissions: editRoleData.permissions
        })
      });

      const result = await response.json();
      
      if (result.success) {
        await loadRoles();
        setIsEditModalOpen(false);
        showSuccess(`Role "${editRoleData.name}" has been updated successfully`, 'Role Updated');
      } else {
        showError(result.error || 'Failed to update role', 'Update Failed');
      }
    } catch (err) {
      console.error('Update role error:', err);
      showError('Network error occurred while updating role', 'Update Failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateNewRole = async () => {
    // Validation
    if (!createRoleData.name.trim()) {
      showWarning('Role name is required', 'Validation Error');
      return;
    }

    if (createRoleData.permissions.length === 0) {
      showWarning('At least one permission must be selected', 'Validation Error');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // For now, use direct fetch to avoid type issues
      const token = localStorage.getItem('nuroveli_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/admin/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: createRoleData.name,
          description: createRoleData.description,
          permissions: createRoleData.permissions
        })
      });

      const result = await response.json();
      
      if (result.success) {
        await loadRoles();
        setIsCreateModalOpen(false);
        showSuccess(`Role "${createRoleData.name}" has been created successfully`, 'Role Created');
      } else {
        showError(result.error || 'Failed to create role', 'Creation Failed');
      }
    } catch (err) {
      console.error('Create role error:', err);
      showError('Network error occurred while creating role', 'Creation Failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      setIsSubmitting(true);
      const response = await adminApi.deleteRole(roleToDelete.id);
      
      if (response.success) {
        await loadRoles();
        showSuccess(`Role "${roleToDelete.name}" has been deleted successfully`, 'Role Deleted');
      } else {
        const errorMsg = extractErrorMessage(response);
        showError(errorMsg, 'Delete Failed');
      }
    } catch (err) {
      console.error('Delete role error:', err);
      showError('Network error occurred while deleting role', 'Delete Failed');
    } finally {
      setIsSubmitting(false);
      setIsDeleteModalOpen(false);
      setRoleToDelete(null);
    }
  };

  const handlePermissionToggle = (permission: string, isCreate: boolean = false) => {
    if (isCreate) {
      setCreateRoleData(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permission)
          ? prev.permissions.filter(p => p !== permission)
          : [...prev.permissions, permission]
      }));
    } else {
      setEditRoleData(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permission)
          ? prev.permissions.filter(p => p !== permission)
          : [...prev.permissions, permission]
      }));
    }
  };

  const handleCategoryToggle = (category: string, isCreate: boolean = false) => {
    const categoryPermissions = permissionStructure[category]?.permissions || [];
    const currentPermissions = isCreate ? createRoleData.permissions : editRoleData.permissions;
    
    const allCategorySelected = categoryPermissions.every(p => currentPermissions.includes(p));
    
    if (allCategorySelected) {
      // Remove all category permissions
      if (isCreate) {
        setCreateRoleData(prev => ({
          ...prev,
          permissions: prev.permissions.filter(p => !categoryPermissions.includes(p))
        }));
      } else {
        setEditRoleData(prev => ({
          ...prev,
          permissions: prev.permissions.filter(p => !categoryPermissions.includes(p))
        }));
      }
    } else {
      // Add all category permissions
      const uniquePermissions = isCreate 
        ? Array.from(new Set([...createRoleData.permissions, ...categoryPermissions]))
        : Array.from(new Set([...editRoleData.permissions, ...categoryPermissions]));
      
      if (isCreate) {
        setCreateRoleData(prev => ({
          ...prev,
          permissions: uniquePermissions
        }));
      } else {
        setEditRoleData(prev => ({
          ...prev,
          permissions: uniquePermissions
        }));
      }
    }
  };

  if (loading) {
    return (
      <div className="admin-roles-loading">
        <div className="admin-roles-loading-spinner">
          <i className="ri-loader-4-line"></i>
        </div>
        <div className="admin-roles-loading-text">Loading roles...</div>
      </div>
    );
  }

  return (
    <DashboardLayout title="Role Management">
      <div className="admin-roles">
        {/* Header */}
        <div className="admin-roles-header">
          <div className="admin-roles-header-content">
            <h1 className="admin-roles-title">Role Management</h1>
            <p className="admin-roles-subtitle">
              Create and manage user roles with customizable permissions across your platform
            </p>
          </div>
          <div className="admin-roles-stats">
            <div className="admin-roles-stat">
              <div className="admin-roles-stat-value">{roles.length}</div>
              <div className="admin-roles-stat-label">Total Roles</div>
            </div>
            <div className="admin-roles-stat">
              <div className="admin-roles-stat-value">
                {roles.filter(r => r.isEditable).length}
              </div>
              <div className="admin-roles-stat-label">Editable</div>
            </div>
            <div className="admin-roles-stat">
              <div className="admin-roles-stat-value">
                {Object.keys(permissionStructure).length}
              </div>
              <div className="admin-roles-stat-label">Categories</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="admin-roles-actions">
          {(hasPermission('roles_create') || isSuperAdmin()) && (
            <button 
              className="admin-roles-create-btn"
              onClick={handleCreateRole}
              disabled={loading || isSubmitting}
            >
              <i className="ri-add-line"></i>
              <span>Create Role</span>
            </button>
          )}
        </div>

        {/* Roles Table */}
        <div className="admin-roles-table-container">
          {roles.length === 0 ? (
            <div className="admin-roles-empty">
              <i className="ri-shield-user-line"></i>
              <h3>No roles found</h3>
              <p>Create your first role to get started</p>
            </div>
          ) : (
            <div className="admin-roles-table">
              <div className="admin-roles-table-header">
                <div className="admin-roles-table-cell">Role</div>
                <div className="admin-roles-table-cell">Permissions</div>
                <div className="admin-roles-table-cell">Editable</div>
                <div className="admin-roles-table-cell">Users</div>
                <div className="admin-roles-table-cell">Created</div>
                <div className="admin-roles-table-cell">Actions</div>
              </div>
              <div className="admin-roles-table-body">
                {roles.map((role) => (
                  <div key={role.id} className="admin-roles-table-row">
                    <div className="admin-roles-table-cell">
                      <div className="admin-role-info">
                        <div className="admin-role-header">
                          <span 
                            className="admin-role-name-badge"
                            style={{ 
                              backgroundColor: `${getRoleColor(role.name)}20`, 
                              color: getRoleColor(role.name) 
                            }}
                          >
                            {role.name}
                          </span>
                        </div>
                        {role.description && (
                          <div className="admin-role-description">{role.description}</div>
                        )}
                      </div>
                    </div>
                    <div className="admin-roles-table-cell">
                      <div className="admin-role-permissions">
                        <span className="admin-role-permissions-count">
                          {role.permissions.length} permissions
                        </span>
                        <div className="admin-role-permissions-preview">
                          {role.permissions.slice(0, 3).map(permission => (
                            <span key={permission} className="admin-role-permission-tag">
                              {permission.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {role.permissions.length > 3 && (
                            <span className="admin-role-permission-more">
                              +{role.permissions.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="admin-roles-table-cell">
                      <span 
                        className="admin-role-editable-badge"
                        style={{ 
                          backgroundColor: role.isEditable ? '#10b98120' : '#f59e0b20',
                          color: role.isEditable ? '#10b981' : '#f59e0b'
                        }}
                      >
                        {role.isEditable ? 'Yes' : 'System'}
                      </span>
                    </div>
                    <div className="admin-roles-table-cell">
                      <span className="admin-role-users-count">
                        {role.users?.length || 0} users
                      </span>
                    </div>
                    <div className="admin-roles-table-cell">
                      <span className="admin-role-created-date">
                        {formatDate(role.createdAt)}
                      </span>
                    </div>
                    <div className="admin-roles-table-cell">
                      <div className="admin-role-actions">
                        {(hasPermission('roles_edit') || isSuperAdmin()) && role.isEditable && (
                          <button 
                            className="admin-role-action-btn"
                            onClick={() => handleEditRole(role)}
                            title="Edit role"
                            disabled={isSubmitting}
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                        )}
                        {(hasPermission('roles_delete') || isSuperAdmin()) && role.isEditable && (
                          <button 
                            className="admin-role-action-btn admin-role-delete-btn"
                            onClick={() => handleDeleteRole(role)}
                            title="Delete role"
                            disabled={isSubmitting}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {isEditModalOpen && selectedRole && (
          <div className="admin-roles-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
            <div className="admin-roles-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-roles-modal-header">
                <h3>Edit Role</h3>
                <button onClick={() => setIsEditModalOpen(false)}>
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="admin-roles-modal-content">
                <div className="admin-roles-form">
                  <div className="admin-roles-form-group">
                    <label>Role Name</label>
                    <input
                      type="text"
                      value={editRoleData.name}
                      onChange={(e) => setEditRoleData({...editRoleData, name: e.target.value})}
                      placeholder="Enter role name"
                    />
                  </div>
                  <div className="admin-roles-form-group">
                    <label>Description</label>
                    <textarea
                      value={editRoleData.description}
                      onChange={(e) => setEditRoleData({...editRoleData, description: e.target.value})}
                      placeholder="Enter role description (optional)"
                      rows={3}
                    />
                  </div>
                  <div className="admin-roles-form-group">
                    <label>Permissions</label>
                    
                    <div className="admin-roles-permissions-container">
                      {Object.entries(permissionStructure).map(([category, data]) => (
                        <div key={category} className="admin-roles-permission-category">
                          <div className="admin-roles-permission-category-header">
                            <label className="admin-roles-checkbox-label category-header">
                              <input
                                type="checkbox"
                                checked={data.permissions.every(p => editRoleData.permissions.includes(p))}
                                onChange={() => handleCategoryToggle(category, false)}
                              />
                              <span className="admin-roles-category-name">{category}</span>
                            </label>
                            <p className="admin-roles-category-description">{data.description}</p>
                          </div>
                          <div className="admin-roles-permission-list">
                            {data.permissions.map(permission => (
                              <label key={permission} className="admin-roles-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={editRoleData.permissions.includes(permission)}
                                  onChange={() => handlePermissionToggle(permission, false)}
                                />
                                <span>{permission.replace(/_/g, ' ')}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="admin-roles-form-actions">
                    <button 
                      className="admin-roles-btn admin-roles-btn-secondary"
                      onClick={() => setIsEditModalOpen(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="admin-roles-create-btn"
                      onClick={handleSaveRole}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Modal */}
        {isCreateModalOpen && (
          <div className="admin-roles-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
            <div className="admin-roles-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-roles-modal-header">
                <h3>Create New Role</h3>
                <button onClick={() => setIsCreateModalOpen(false)}>
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="admin-roles-modal-content">
                <div className="admin-roles-form">
                  <div className="admin-roles-form-group">
                    <label>Role Name</label>
                    <input
                      type="text"
                      value={createRoleData.name}
                      onChange={(e) => setCreateRoleData({...createRoleData, name: e.target.value})}
                      placeholder="Enter role name"
                    />
                  </div>
                  <div className="admin-roles-form-group">
                    <label>Description</label>
                    <textarea
                      value={createRoleData.description}
                      onChange={(e) => setCreateRoleData({...createRoleData, description: e.target.value})}
                      placeholder="Enter role description (optional)"
                      rows={3}
                    />
                  </div>
                  <div className="admin-roles-form-group">
                    <label>Permissions</label>
                    
                    <div className="admin-roles-permissions-container">
                      {Object.entries(permissionStructure).map(([category, data]) => (
                        <div key={category} className="admin-roles-permission-category">
                          <div className="admin-roles-permission-category-header">
                            <label className="admin-roles-checkbox-label category-header">
                              <input
                                type="checkbox"
                                checked={data.permissions.every(p => createRoleData.permissions.includes(p))}
                                onChange={() => handleCategoryToggle(category, true)}
                              />
                              <span className="admin-roles-category-name">{category}</span>
                            </label>
                            <p className="admin-roles-category-description">{data.description}</p>
                          </div>
                          <div className="admin-roles-permission-list">
                            {data.permissions.map(permission => (
                              <label key={permission} className="admin-roles-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={createRoleData.permissions.includes(permission)}
                                  onChange={() => handlePermissionToggle(permission, true)}
                                />
                                <span>{permission.replace(/_/g, ' ')}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="admin-roles-form-actions">
                    <button 
                      className="admin-roles-btn admin-roles-btn-secondary"
                      onClick={() => setIsCreateModalOpen(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="admin-roles-create-btn"
                      onClick={handleCreateNewRole}
                      disabled={isSubmitting || !createRoleData.name}
                    >
                      {isSubmitting ? 'Creating...' : 'Create Role'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDeleteRole}
          title="Delete Role"
          message="Are you sure you want to delete this role? This action cannot be undone."
          confirmText="Delete Role"
          type="danger"
          isLoading={isSubmitting}
        >
          {roleToDelete && (
            <div className="admin-roles-delete-modal-role">
              <div className="admin-roles-delete-modal-role-info">
                <h4>{roleToDelete.name}</h4>
                {roleToDelete.description && <p>{roleToDelete.description}</p>}
                <div className="admin-roles-delete-modal-role-meta">
                  <span className="admin-roles-delete-modal-role-permissions">
                    {roleToDelete.permissions.length} permissions
                  </span>
                  <span className="admin-roles-delete-modal-role-users">
                    {roleToDelete.users?.length || 0} users assigned
                  </span>
                </div>
              </div>
            </div>
          )}
        </ConfirmationModal>
      </div>
    </DashboardLayout>
  );
};

export default AdminRoleManagement;